import { Dispatch, SetStateAction, useContext, useState } from "react"

import { ApplicationContext } from "../../../../components/submitApplication/single/applicationProvider"
import {
    UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE,
    UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE_STRING,
} from "../../../../constants/generalConstants"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import { scrollToTop } from "../../../../utils/submitApplication/submitApplicationUtils"
import { postClientProtected } from "../../clients/clients"
import { getSchemaMapper } from "../../mappers/singleApplication/getSchemaMapper"
import { isSubmitApplicationResponse } from "../../types/singleApplication/submitApplictionTypes"
import {
    CREATE_APPLICATION,
    INSTALLER_SUBMIT_AMENDMENT,
} from "../../urls/hybrid"

interface ISubmitApplication {
    isSubmitting: boolean
    setIsSubmitting: Dispatch<SetStateAction<boolean>>
    isSuccessDialogOpen: boolean
    setIsMpanNotFoundDialogOpen: Dispatch<SetStateAction<boolean>>
    isMpanNotFoundDialogOpen: boolean
    error: IAppError | undefined
    postApplication: (manuallyProvidedMpan?: string) => Promise<void>
    dno: string
    applicationId: string
}

/**
 * Provides functionality for submitting a single connections application.
 * @returns {ISubmitApplication} - loading and error states.
 */
const useSubmitApplication = (): ISubmitApplication => {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [dno, setDno] = useState<string>("")
    const [applicationId, setApplicationId] = useState<string>("")
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false)
    const [isMpanNotFoundDialogOpen, setIsMpanNotFoundDialogOpen] =
        useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)

    const {
        formData,
        fixedInputs,
        rawApplicationSpec,
        isAmendmentMode,
        amendmentApplicationId,
    } = useContext(ApplicationContext)
    const { draftId } = useContext(ApplicationContext) // Access draftId from context
    const postApplication = async (
        manuallyProvidedMpan?: string
    ): Promise<void> => {
        let formDataForSubmission

        // Handle manually provided MPAN (if available)
        if (manuallyProvidedMpan) {
            formDataForSubmission = {
                ...formData,
                installerCustomerDetails: {
                    ...formData.installerCustomerDetails,
                    mpan: manuallyProvidedMpan,
                },
            }
        } else {
            formDataForSubmission = formData
        }

        try {
            setIsSubmitting(true)

            // Prepare data for submission (excluding draftId from body)
            const submitData = {
                ...formDataForSubmission,
                ...fixedInputs,
            }

            const body = getSchemaMapper.splitMergedProperties(
                { ...submitData },
                rawApplicationSpec
            )

            // Construct the URL with draftId as a query param (if it exists)
            const url = draftId
                ? `${CREATE_APPLICATION}?draftId=${encodeURIComponent(draftId)}`
                : CREATE_APPLICATION

            let res

            // Amendment flow
            if (isAmendmentMode && amendmentApplicationId) {
                res = await postClientProtected(
                    INSTALLER_SUBMIT_AMENDMENT(amendmentApplicationId),
                    body
                )
                if (isSubmitApplicationResponse(res)) {
                    setDno(res.dno)
                    setApplicationId(amendmentApplicationId)
                }
            } else {
                // Creation flow
                res = await postClientProtected(url, body)
                if (isSubmitApplicationResponse(res)) {
                    setDno(res.dno)
                    setApplicationId(res.applicationId)
                }
            }

            setIsMpanNotFoundDialogOpen(false)
            setIsSuccessDialogOpen(true)
            setError(undefined)
            scrollToTop(document)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)

            // We want to close the MPAN dialog on the "review" point of the
            // journey if we've submitted the application from it, and it
            // returns an error which isn't related to the MPAN number.
            // This will enable the user to see the error at "review"

            if (
                isMpanNotFoundDialogOpen &&
                errorObj.code !== UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE &&
                errorObj.code !==
                    UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE_STRING
            ) {
                setIsMpanNotFoundDialogOpen(false)
            }

            if (
                errorObj.code === UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE ||
                errorObj.code ===
                    UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE_STRING
            ) {
                setIsMpanNotFoundDialogOpen(true)
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    return {
        isSubmitting,
        setIsSubmitting,
        isSuccessDialogOpen,
        setIsMpanNotFoundDialogOpen,
        isMpanNotFoundDialogOpen,
        error,
        postApplication,
        dno,
        applicationId,
    }
}

export default useSubmitApplication
